<template>
  <div class="container-fluid py-4">
    <!-- Header with view toggle -->
    <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
      <div>
        <h4 class="mb-1">{{ $t('golf.mapTitle') }}</h4>
        <small class="text-muted">
          {{ activeView === 'cards'
            ? $t('golf.cardViewHint')
            : $t('golf.mapViewHint') }}
        </small>
      </div>
      <div class="d-flex gap-2 align-items-center">
        <select v-model="selectedCourseId" class="form-select form-select-sm" style="width: auto;">
          <option v-for="c in courses" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
      </div>
      <div class="btn-group" role="group">
        <button
          type="button"
          class="btn btn-sm"
          :class="activeView === 'cards' ? 'btn-dark' : 'btn-outline-dark'"
          @click="activeView = 'cards'"
        >
          <i class="material-icons" style="font-size: 16px; vertical-align: middle;">grid_view</i>
          {{ $t('golf.holes') }}
        </button>
        <button
          type="button"
          class="btn btn-sm"
          :class="activeView === 'map' ? 'btn-dark' : 'btn-outline-dark'"
          @click="switchToMap"
        >
          <i class="material-icons" style="font-size: 16px; vertical-align: middle;">satellite_alt</i>
          {{ $t('golf.mapView') }}
        </button>
      </div>
      <button v-if="isAdmin" class="btn btn-success btn-sm mb-0" @click="showCreateTask = true">
        <i class="material-icons align-middle" style="font-size:18px;">add</i>
        {{ $t('golf.createTask') }}
      </button>
    </div>

    <!-- Card View -->
    <template v-if="activeView === 'cards'">
      <!-- Hole Grid: 3 rows x 6 cols for 18 holes -->
      <div class="row mb-4">
        <div
          v-for="hole in holes"
          :key="hole.id"
          class="col-lg-2 col-md-3 col-4 mb-3"
        >
          <div
            class="hole-card text-center p-3 rounded position-relative"
            :class="{
              'hole-card--active': holeTaskCount(hole) > 0,
              'hole-card--selected': selectedHole && selectedHole.id === hole.id,
            }"
            style="cursor: pointer;"
            @click="selectHole(hole)"
          >
            <span
              v-if="holeTaskCount(hole) > 0"
              class="position-absolute badge bg-danger rounded-pill"
              style="top: -6px; right: -6px; font-size: 0.75rem;"
            >
              {{ holeTaskCount(hole) }}
            </span>
            <div class="fw-bold" style="font-size: 2rem; line-height: 1.2;">
              {{ hole.holeNumber }}
            </div>
            <small class="text-muted">{{ $t('golf.hole') }}</small>
          </div>
        </div>
      </div>

      <!-- All Tasks List -->
      <div class="card">
        <div class="card-body">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h6 class="fw-bold mb-0">
              {{ selectedHole ? `${$t('golf.hole')} ${selectedHole.holeNumber}` : $t('golf.allTasks') }}
            </h6>
            <ul class="nav nav-pills nav-pills-sm">
              <li class="nav-item">
                <button
                  class="nav-link py-1 px-3"
                  :class="{ active: taskFilter === 'open' }"
                  @click="taskFilter = 'open'"
                >{{ $t('golf.openTasks') }}</button>
              </li>
              <li class="nav-item">
                <button
                  class="nav-link py-1 px-3"
                  :class="{ active: taskFilter === 'completed' }"
                  @click="taskFilter = 'completed'"
                >{{ $t('golf.completedTasks') }}</button>
              </li>
            </ul>
          </div>

          <div v-if="filteredTasks.length === 0" class="text-center py-4">
            <i class="material-icons opacity-50" style="font-size:48px;">check_circle</i>
            <p class="text-muted mt-2">{{ $t('golf.noTasks') }}</p>
          </div>

          <div v-else>
            <div
              v-for="task in filteredTasks"
              :key="task.id"
              class="d-flex align-items-start p-3 mb-2 border rounded task-row"
              :class="{ 'bg-light': task.isCompleted }"
              style="cursor: pointer;"
              @click="openTaskDetail(task)"
            >
              <i
                class="material-icons me-3 mt-1"
                :class="task.isCompleted ? 'text-success' : 'text-warning'"
                style="font-size: 24px;"
              >
                {{ task.isCompleted ? 'check_circle' : 'warning' }}
              </i>

              <div class="flex-grow-1">
                <strong :class="{ 'text-decoration-line-through text-muted': task.isCompleted }">
                  {{ task.title }}
                </strong>
                <div v-if="task.description" class="small text-muted mt-1">
                  {{ task.description }}
                </div>
                <div class="small text-muted mt-1">
                  <span v-if="task.holeNumber">{{ $t('golf.hole') }} {{ task.holeNumber }}</span>
                  <span v-if="task.holeNumber && task.assignedToName"> &middot; </span>
                  <span v-if="task.assignedToName">{{ task.assignedToName }}</span>
                  <span v-if="task.createdAt"> &middot; {{ formatDate(task.createdAt) }}</span>
                </div>
              </div>

              <div class="ms-3 flex-shrink-0">
                <span
                  class="badge"
                  :class="task.isCompleted ? 'bg-success' : 'bg-warning text-dark'"
                >
                  {{ task.isCompleted ? $t('golf.completed') : $t('golf.inProgress') }}
                </span>
                <button
                  v-if="!task.isCompleted"
                  class="btn btn-success btn-sm d-block mt-2"
                  style="font-size: 0.75rem;"
                  @click.stop="completeTask(task)"
                >
                  {{ $t('golf.completeTask') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Map View -->
    <template v-if="activeView === 'map'">
      <div class="card">
        <div class="card-body p-0">
          <div id="golfSatelliteMap" class="golf-map"></div>
        </div>
      </div>

      <!-- Legend -->
      <div class="card mt-3">
        <div class="card-body py-2 px-3">
          <div class="d-flex flex-wrap align-items-center gap-4">
            <span class="d-flex align-items-center">
              <span class="legend-dot bg-danger me-2"></span>
              {{ $t('golf.openTasks') }}
            </span>
            <span class="d-flex align-items-center">
              <span class="legend-dot bg-info me-2"></span>
              {{ $t('golf.imagesWithGPS') }}
            </span>
          </div>
        </div>
      </div>
    </template>

    <!-- Create Task Modal -->
    <modal v-model:show="showCreateTask" class="white-content">
      <h5 class="mb-3">{{ $t('golf.createTask') }}</h5>
      <div class="mb-3">
        <label class="form-label">{{ $t('golf.taskTitle') }}</label>
        <input v-model="newTask.title" type="text" class="form-control" />
      </div>
      <div class="mb-3">
        <label class="form-label">{{ $t('common.description') }}</label>
        <textarea v-model="newTask.description" class="form-control" rows="3"></textarea>
      </div>
      <div class="mb-3">
        <label class="form-label">{{ $t('golf.hole') }}</label>
        <select v-model="newTask.golfHoleId" class="form-select">
          <option :value="null">--</option>
          <option v-for="h in holes" :key="h.id" :value="h.id">#{{ h.holeNumber }} {{ h.name || '' }}</option>
        </select>
      </div>
      <div class="mb-3">
        <label class="form-label">{{ $t('golf.takePhoto') }}</label>
        <input
          ref="photoInput"
          type="file"
          accept="image/*"
          capture="environment"
          class="form-control"
          @change="onPhotoSelected"
        />
        <div v-if="photoPreview" class="mt-2">
          <img :src="photoPreview" alt="Preview" class="img-fluid rounded" style="max-height: 200px;" />
        </div>
      </div>
      <button class="btn btn-success w-100" :disabled="!newTask.title || creatingTask" @click="createTask">
        <span v-if="creatingTask" class="spinner-border spinner-border-sm me-1"></span>
        <span v-if="uploadStage === 'compressing'">{{ $t('common.compressing', { done: 1, total: 1 }) }}</span>
        <span v-else-if="uploadStage === 'uploading'">{{ $t('common.uploading') }}</span>
        <span v-else>{{ $t('golf.createTask') }}</span>
      </button>
    </modal>

    <!-- Task Detail Modal -->
    <modal v-model:show="showTaskDetail" class="white-content">
      <template v-if="selectedTask">
        <div class="d-flex align-items-start justify-content-between mb-2">
          <h5 class="mb-0 me-3">{{ selectedTask.title }}</h5>
          <span
            class="badge flex-shrink-0"
            :class="selectedTask.isCompleted ? 'bg-success' : 'bg-warning text-dark'"
          >
            {{ selectedTask.isCompleted ? $t('golf.completed') : $t('golf.inProgress') }}
          </span>
        </div>

        <div class="small text-muted mb-3">
          <span v-if="selectedTask.holeNumber">{{ $t('golf.hole') }} {{ selectedTask.holeNumber }}</span>
          <span v-if="selectedTask.holeNumber && selectedTask.assignedToName"> &middot; </span>
          <span v-if="selectedTask.assignedToName">{{ $t('golf.assigned') }}: {{ selectedTask.assignedToName }}</span>
          <span v-if="selectedTask.createdAt"> &middot; {{ formatDate(selectedTask.createdAt) }}</span>
        </div>

        <div v-if="selectedTask.description" class="mb-3">
          <label class="form-label small fw-bold text-muted mb-1">{{ $t('common.description') }}</label>
          <div class="p-2 bg-light rounded">{{ selectedTask.description }}</div>
        </div>

        <div v-if="selectedTask.images && selectedTask.images.length > 0" class="mb-3">
          <div class="d-flex flex-wrap gap-2">
            <a
              v-for="img in selectedTask.images"
              :key="img.id"
              :href="img.downloadUrl"
              target="_blank"
              rel="noopener"
            >
              <img
                :src="img.downloadUrl"
                :alt="img.originalFilename"
                class="rounded border"
                style="max-height: 200px; max-width: 100%;"
                loading="lazy"
              />
            </a>
          </div>
        </div>

        <div class="d-flex gap-2">
          <button
            v-if="!selectedTask.isCompleted"
            class="btn btn-success flex-grow-1"
            @click="completeTaskFromDetail"
          >
            {{ $t('golf.completeTask') }}
          </button>
          <button class="btn btn-secondary" @click="showTaskDetail = false">
            {{ $t('common.close') }}
          </button>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import { golfApi as api } from "../../services/api";
import { getCustomerConfig } from '../Settings/customerConfigHelper';
import { compressImagesForUpload, uploadToPresignedUrl } from '../../services/imageUpload';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import Modal from "../../components/Modals/Modal.vue";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
});

export default {
  components: {
    Modal,
  },
  data() {
    return {
      courses: [],
      selectedCourseId: null,
      defaultGolfCourseId: null,
      holes: [],
      tasks: [],
      selectedHole: null,
      taskFilter: 'open',
      activeView: 'cards',
      map: null,
      markerLayer: null,
      mapInitialized: false,
      showCreateTask: false,
      newTask: { title: '', description: '', golfHoleId: null },
      selectedPhoto: null,
      photoPreview: null,
      creatingTask: false,
      uploadStage: null,
      deviceLocation: null,
      showTaskDetail: false,
      selectedTask: null,
    };
  },
  computed: {
    isAdmin() {
      try {
        const token = Cookies.get('accessToken');
        if (!token) return false;
        const role = jwtDecode(token).role;
        return role === 'Admin' || role === 'SuperAdmin';
      } catch { return false; }
    },
    filteredTasks() {
      let base;
      if (this.selectedHole) {
        base = this.tasks.filter(t => t.golfHoleId === this.selectedHole.id);
      } else {
        base = this.tasks.filter(t => !t.golfHoleId);
      }
      if (this.taskFilter === 'open') {
        return base.filter(t => !t.isCompleted);
      }
      return base.filter(t => t.isCompleted);
    },
  },
  watch: {
    selectedCourseId() {
      this.fetchHolesAndTasks();
      // Re-center map if already initialized
      if (this.map && this.mapInitialized) {
        const course = this.courses.find(c => c.id === this.selectedCourseId);
        if (course && course.latitude && course.longitude) {
          this.map.setView([course.latitude, course.longitude], 14);
        }
      }
    },
    activeView(_, oldView) {
      // v-if unmounts #golfSatelliteMap when leaving map view; destroy the
      // Leaflet instance so the next visit re-inits against the new DOM node.
      if (oldView === 'map' && this.map) {
        this.map.remove();
        this.map = null;
        this.markerLayer = null;
        this.mapInitialized = false;
      }
    },
  },
  async created() {
    const val = await getCustomerConfig('DefaultGolfCourseId');
    this.defaultGolfCourseId = val ? Number(val) : null;
    this.fetchData();
    this.requestDeviceLocation();
  },
  beforeUnmount() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    requestDeviceLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            this.deviceLocation = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
            };
          },
          () => { /* user denied or unavailable */ }
        );
      }
    },
    onPhotoSelected(event) {
      const file = event.target.files[0];
      if (!file) {
        this.selectedPhoto = null;
        this.photoPreview = null;
        return;
      }
      this.selectedPhoto = file;
      this.photoPreview = URL.createObjectURL(file);
    },
    async createTask() {
      this.creatingTask = true;
      try {
        const res = await api.post('/tasks', {
          title: this.newTask.title,
          description: this.newTask.description || null,
          golfHoleId: this.newTask.golfHoleId,
          latitude: this.deviceLocation?.latitude || null,
          longitude: this.deviceLocation?.longitude || null,
        });

        const taskId = res.data.id;

        if (this.selectedPhoto && taskId) {
          await this.uploadTaskPhoto(taskId, this.selectedPhoto);
        }

        this.$toast.success(this.$t('common.created'));
        this.showCreateTask = false;
        this.newTask = { title: '', description: '', golfHoleId: null };
        this.selectedPhoto = null;
        this.photoPreview = null;
        this.fetchHolesAndTasks();
      } catch (error) {
        console.error('Failed to create task:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.creatingTask = false;
      }
    },
    async uploadTaskPhoto(taskId, file) {
      try {
        this.uploadStage = 'compressing';
        const [prepared] = await compressImagesForUpload([file]);

        this.uploadStage = 'uploading';
        const imageRes = await api.post(`/tasks/${taskId}/images`, {
          golfTaskId: taskId,
          filename: prepared.filename,
          contentType: prepared.contentType,
          deviceGPSLatitude: this.deviceLocation?.latitude || null,
          deviceGPSLongitude: this.deviceLocation?.longitude || null,
        });

        const { uploadUrl } = imageRes.data;

        await uploadToPresignedUrl(uploadUrl, prepared.blob, prepared.contentType);
      } catch (error) {
        console.error('Failed to upload task photo:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('golf.uploadError'), { timeout: 3000 });
      } finally {
        this.uploadStage = null;
      }
    },
    async fetchData() {
      try {
        const coursesRes = await api.get('/courses');
        this.courses = coursesRes.data;
        if (this.courses.length > 0 && !this.selectedCourseId) {
          const defaultCourse = this.defaultGolfCourseId
            && this.courses.find(c => c.id === this.defaultGolfCourseId);
          this.selectedCourseId = defaultCourse ? defaultCourse.id : this.courses[0].id;
        } else {
          this.fetchHolesAndTasks();
        }
      } catch (error) {
        console.error('Failed to load courses:', error); // eslint-disable-line no-console
      }
    },

    async fetchHolesAndTasks() {
      try {
        const holesUrl = this.selectedCourseId ? `/holes?courseId=${this.selectedCourseId}` : '/holes';
        const [holesRes, tasksRes] = await Promise.all([
          api.get(holesUrl),
          api.get('/tasks?includeCompleted=true'),
        ]);
        this.holes = holesRes.data;
        this.tasks = tasksRes.data;
        // Refresh map markers if map is active
        if (this.markerLayer) {
          this.markerLayer.clearLayers();
          this.addTaskMarkers();
          this.addImageMarkers();
        }
      } catch (error) {
        console.error('Failed to load map data:', error); // eslint-disable-line no-console
      }
    },

    holeTaskCount(hole) {
      return this.tasks.filter(t => t.golfHoleId === hole.id && !t.isCompleted).length;
    },

    selectHole(hole) {
      this.selectedHole = this.selectedHole?.id === hole.id ? null : hole;
    },

    async completeTask(task) {
      try {
        await api.put(`/tasks/${task.id}/complete`);
        task.isCompleted = true;
        this.$toast.success(this.$t('golf.completed'));
      } catch (error) {
        console.error('Failed to complete task:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },

    openTaskDetail(task) {
      this.selectedTask = task;
      this.showTaskDetail = true;
    },

    async completeTaskFromDetail() {
      if (!this.selectedTask) return;
      await this.completeTask(this.selectedTask);
      this.showTaskDetail = false;
    },

    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString('is-IS', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },

    switchToMap() {
      this.activeView = 'map';
      this.$nextTick(() => {
        this.initMap();
      });
    },

    initMap() {
      if (this.mapInitialized) {
        // Map already exists, just invalidate size
        this.map.invalidateSize();
        return;
      }

      const el = document.getElementById('golfSatelliteMap');
      if (!el) return;

      // Center on selected course coordinates, fallback to Akureyri Golf Club
      const course = this.courses.find(c => c.id === this.selectedCourseId);
      const center = (course && course.latitude && course.longitude)
        ? [course.latitude, course.longitude]
        : [65.6685, -18.1115];

      this.map = L.map('golfSatelliteMap', {
        center: center,
        zoom: 14,
        zoomControl: true,
      });

      // Satellite layer (Esri World Imagery — free, no API key)
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19,
        attribution: 'Tiles &copy; Esri'
      }).addTo(this.map);

      // Optional: add labels on top of satellite
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19,
      }).addTo(this.map);

      this.markerLayer = L.layerGroup().addTo(this.map);
      this.addTaskMarkers();
      this.addImageMarkers();
      this.mapInitialized = true;
    },

    addTaskMarkers() {
      // Only show unfinished tasks on the map
      const tasksWithGPS = this.tasks.filter(t => !t.isCompleted && t.latitude && t.longitude);

      tasksWithGPS.forEach(task => {
        const icon = L.divIcon({
          className: 'golf-marker-icon',
          html: `<div class="golf-marker golf-marker--open">
            <i class="material-icons" style="font-size:16px;">warning</i>
          </div>`,
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        });

        const marker = L.marker([task.latitude, task.longitude], { icon }).addTo(this.markerLayer);

        const holeText = task.holeNumber ? `${this.$t('golf.hole')} ${task.holeNumber}` : '';
        const dateText = this.formatDate(task.createdAt);

        // Show thumbnail of first image if available
        const firstImage = task.images && task.images[0];
        const thumbHtml = firstImage && firstImage.downloadUrl
          ? `<img src="${firstImage.downloadUrl}" style="width:100%;max-width:200px;border-radius:4px;margin-bottom:6px;display:block;" loading="lazy" />`
          : '';

        const popupContent = document.createElement('div');
        popupContent.style.minWidth = '180px';
        popupContent.style.cursor = 'pointer';
        popupContent.innerHTML = `
            ${thumbHtml}
            <strong>${task.title}</strong>
            ${task.description ? `<br><small class="text-muted">${task.description}</small>` : ''}
            <hr style="margin:6px 0;">
            ${holeText ? `<small>${holeText}</small><br>` : ''}
            ${task.assignedToName ? `<small>${this.$t('golf.assigned')}: ${task.assignedToName}</small><br>` : ''}
            <small>${dateText}</small>
            <hr style="margin:6px 0;">
            <a href="#" class="popup-detail-link" style="font-size:0.8rem;font-weight:600;">${this.$t('golf.viewDetails')} &rarr;</a>
        `;

        const detailLink = popupContent.querySelector('.popup-detail-link');
        detailLink.addEventListener('click', (e) => {
          e.preventDefault();
          marker.closePopup();
          this.openTaskDetail(task);
        });

        marker.bindPopup(popupContent);
      });
    },

    addImageMarkers() {
      this.tasks.forEach(task => {
        if (!task.images) return;

        task.images.forEach(img => {
          // Prefer device GPS, fall back to image EXIF GPS
          const lat = img.deviceGPSLatitude || img.imageGPSLatitude;
          const lng = img.deviceGPSLongitude || img.imageGPSLongitude;
          if (!lat || !lng) return;

          // Skip if image is at the same location as the task (avoid overlap)
          if (task.latitude && task.longitude &&
              Math.abs(task.latitude - lat) < 0.0001 &&
              Math.abs(task.longitude - lng) < 0.0001) return;

          const icon = L.divIcon({
            className: 'golf-marker-icon',
            html: `<div class="golf-marker golf-marker--image">
              <i class="material-icons" style="font-size:16px;">photo_camera</i>
            </div>`,
            iconSize: [28, 28],
            iconAnchor: [14, 14],
          });

          const marker = L.marker([lat, lng], { icon }).addTo(this.markerLayer);

          const thumbHtml = img.downloadUrl
            ? `<img src="${img.downloadUrl}" style="width:100%;max-width:200px;border-radius:4px;margin-bottom:6px;" /><br>`
            : '';

          marker.bindPopup(`
            <div style="min-width:160px;">
              ${thumbHtml}
              <small><strong>${img.originalFilename}</strong></small><br>
              <small>${this.$t('golf.task')}: ${task.title}</small><br>
              <small>${this.formatDate(img.uploadedAt)}</small>
            </div>
          `);
        });
      });
    },
  },
};
</script>

<style scoped>
.hole-card {
  border: 2px solid #e9ecef;
  background: #fff;
  transition: all 0.15s ease;
}

.hole-card:hover {
  border-color: #28a745;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.15);
}

.hole-card--active {
  border-color: #dc3545;
  background: #fff5f5;
}

.hole-card--active:hover {
  border-color: #dc3545;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.2);
}

.hole-card--selected {
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.25);
}

.golf-map {
  height: 70vh;
  min-height: 400px;
  width: 100%;
  border-radius: 0.75rem;
  overscroll-behavior: contain;
  touch-action: none;
}

.legend-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.form-select {
  border: 1px solid #d2d6da !important;
}

.form-select:focus {
  border-color: #4caf50 !important;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2) !important;
}

/* ── Dark mode ── */
body.dark-version .hole-card { background: #202940; border-color: rgba(255,255,255,.1); }
body.dark-version .hole-card:hover { border-color: #28a745; }
body.dark-version .hole-card--active { background: rgba(220,53,69,.15); border-color: #dc3545; }
</style>

<style>
/* Unscoped so Leaflet markers pick them up */
.golf-marker-icon {
  background: none !important;
  border: none !important;
}

.golf-marker {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  border: 2px solid #fff;
}

.golf-marker--open {
  background: #dc3545;
}

.golf-marker--completed {
  background: #28a745;
}

.golf-marker--image {
  background: #17a2b8;
  width: 28px;
  height: 28px;
}
</style>
